import { hue, initials, PageTabs } from './shell.js';
import type { ComponentChildren, Ref } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { ApiError } from './api.js';
import { useT, type Dict } from './i18n.js';

export function Block(p: { title: string; info?: string; actions?: ComponentChildren; children: ComponentChildren; id?: string; tour?: string }) {
  return (
    <section class="blk" aria-labelledby={p.id ? `${p.id}-h` : undefined} data-tour={p.tour}>
      <div class="blk-head">
        <h2 class="blk-title" id={p.id ? `${p.id}-h` : undefined}>
          {p.title}
        </h2>
        {p.actions ? <div class="blk-actions">{p.actions}</div> : null}
      </div>
      {p.info ? <p class="blk-info">{p.info}</p> : null}
      {p.children}
    </section>
  );
}

/** A secondary list, folded by default: title with a count, the explanation and list inside. */
export function Fold(p: { title: string; count: number; info?: string; children: ComponentChildren }) {
  return (
    <details class="blk fold">
      <summary>
        <span class="blk-title">{p.title}</span> <span class="fold-n">{p.count}</span>
      </summary>
      {p.info ? <p class="blk-info">{p.info}</p> : null}
      {p.children}
    </details>
  );
}

/**
 * "More options": the less used fields of a form, folded under one line so the default form shows only what is needed
 * to finish. The line counts what is filled in inside; each person's open or closed choice is remembered per form
 * (`id`), and `open` forces it open, e.g. when a field inside has an error.
 */
export function MoreOptions(p: { id: string; filled?: number; open?: boolean; children: ComponentChildren }) {
  const t = useT();
  const key = `kff.more.${p.id}`;
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const toggle = (e: Event) => {
    const now = (e.currentTarget as HTMLDetailsElement).open;
    if (now === open) return;
    setOpen(now);
    try {
      if (now) localStorage.setItem(key, '1');
      else localStorage.removeItem(key);
    } catch {
      /* private window: the choice is just not remembered */
    }
  };
  return (
    <details class="more" open={open || p.open} onToggle={toggle}>
      <summary>
        {t.common.moreOptions}
        {p.filled ? <span class="more-n">{t.common.filled(p.filled)}</span> : null}
      </summary>
      <div class="more-body">{p.children}</div>
    </details>
  );
}

export function Callout(p: { tone: 'info' | 'warn' | 'ok'; title: string; children?: ComponentChildren }) {
  return (
    <div class={`callout callout--${p.tone}`} role={p.tone === 'warn' ? 'status' : undefined}>
      <strong>{p.title}</strong>
      {p.children ? <div>{p.children}</div> : null}
    </div>
  );
}

export const Pill = (p: { tone: 'ok' | 'warn' | 'crit' | 'info' | 'neutral'; children: ComponentChildren }) => <span class={`pill pill--${p.tone}`}>{p.children}</span>;

export interface PersonRef {
  id?: string;
  name: string;
  /** hover text in a stack instead of the bare name, e.g. "Mira Ek · Editor" */
  title?: string;
}
type AvatarSize = 'sm' | 'md' | 'lg';

/**
 * A person as a round ball with their initials (AF, CF …): same name, same colour everywhere in the Hub, the full name
 * on hover. `label` replaces the hover text (e.g. "Producer: Anna Falk"); `decorative` hides it from screen readers when
 * the name is written next to it. `client` draws an outlined ball so client contacts never read as KFF staff.
 */
export function Avatar(p: { name: string; size?: AvatarSize; label?: string; decorative?: boolean; client?: boolean }) {
  const label = p.label ?? p.name;
  return (
    <span
      class={`avatar${p.size && p.size !== 'md' ? ` avatar--${p.size}` : ''}${p.client ? ' avatar--client' : ''}`}
      style={`--h:${hue(p.name)}`}
      title={label}
      {...(p.decorative ? { 'aria-hidden': 'true' } : { role: 'img', 'aria-label': label })}
    >
      {initials(p.name) || '?'}
    </span>
  );
}

/** Several people as overlapping balls; past `max` the rest fold into "+N" (their names on hover). */
export function AvatarStack(p: { people: PersonRef[]; max?: number; size?: AvatarSize; label?: string }) {
  const seen = new Set<string>();
  const people = p.people.filter((x) => {
    const k = x.id ?? x.name;
    return seen.has(k) ? false : (seen.add(k), true);
  });
  if (!people.length) return null;
  const max = p.max ?? 4;
  // "+1" is no shorter than the ball it hides, so show the last one instead
  const shown = people.length > max + 1 ? people.slice(0, max) : people;
  const rest = people.slice(shown.length);
  const names = people.map((x) => x.name).join(', ');
  return (
    <span class={`avs${p.size ? ` avs--${p.size}` : ''}`} role="img" aria-label={p.label ? `${p.label}: ${names}` : names}>
      {shown.map((x) => (
        <Avatar name={x.name} label={x.title} size={p.size} decorative key={x.id ?? x.name} />
      ))}
      {rest.length ? (
        <span class={`avatar avatar--more${p.size && p.size !== 'md' ? ` avatar--${p.size}` : ''}`} title={rest.map((x) => x.title ?? x.name).join(', ')} aria-hidden="true">
          +{rest.length}
        </span>
      ) : null}
    </span>
  );
}

/**
 * The people in one role, for a per-role column on the overviews (owner 2026-10-21: who does what at a glance): balls only,
 * no names beside them to save room; the full name and role on hover.
 */
export function RolePeople(p: { people: { name: string; role: string }[] }) {
  const seen = [...new Map(p.people.map((x) => [x.name, x])).values()];
  if (!seen.length) return <span class="muted">–</span>;
  if (seen.length === 1)
    return (
      <span class="role-people" title={`${seen[0]!.name} · ${seen[0]!.role}`}>
        <Avatar name={seen[0]!.name} size="sm" decorative />
      </span>
    );
  return <AvatarStack people={seen.map((x) => ({ id: x.name, name: x.name, title: `${x.name} · ${x.role}` }))} max={3} size="sm" label={seen[0]!.role} />;
}

/** A person with their ball and name, e.g. in the team list of a project. */
export function PersonChip(p: { name: string; sub?: string; size?: AvatarSize; client?: boolean }) {
  return (
    <span class="pchip">
      <Avatar name={p.name} size={p.size ?? 'sm'} decorative client={p.client} />
      <span class="pchip-t">
        <span class="pchip-name">{p.name}</span>
        {p.sub ? <span class="pchip-sub">{p.sub}</span> : null}
      </span>
    </span>
  );
}

/**
 * The page's title, its line under it and the page tabs. A page's own button(s) go in `action`, on the title row, so the
 * tabs always get the whole width and never break onto a second line on a phone (owner 2026-11-02).
 */
export function PageHead(p: { title: string; sub?: string; action?: ComponentChildren }) {
  const title = (
    <>
      <h1 class="h1">{p.title}</h1>
      {p.sub ? <p class="sub">{p.sub}</p> : null}
    </>
  );
  return (
    <header class="phead">
      {p.action ? (
        <div class="phead-top">
          <div class="phead-text">{title}</div>
          <div class="phead-act">{p.action}</div>
        </div>
      ) : (
        title
      )}
      <PageTabs />
    </header>
  );
}

/** Loading: a skeleton in the page's shape; the label is announced to screen readers. */
export function Loading(p: { label?: string }) {
  const t = useT();
  return (
    <div class="screen skel" role="status" aria-live="polite">
      <span class="sr-only">{p.label ?? t.common.loading}</span>
      <div class="skel-line skel-line--title" aria-hidden="true" />
      <div class="skel-line" style="width:60%" aria-hidden="true" />
      <div class="skel-blk" aria-hidden="true" />
    </div>
  );
}

/** What went wrong, in words a person can act on: a lost connection or a server hiccup is not shown as raw text. */
export function errorText(e: unknown, t: Dict): string {
  if (e instanceof TypeError) return t.common.errorOffline;
  if (e instanceof ApiError) return e.status >= 500 || e.code === 'bad_response' ? t.common.errorGeneric : e.message;
  return e instanceof Error && e.message ? e.message : t.common.errorGeneric;
}

export function ErrorBox(p: { error: unknown; onRetry?: () => void }) {
  const t = useT();
  return (
    <div class="callout callout--crit error-box" role="alert">
      <span>{errorText(p.error, t)}</span>
      {p.onRetry ? (
        <button type="button" class="btn btn-sm" onClick={p.onRetry}>
          {t.common.retry}
        </button>
      ) : null}
    </div>
  );
}

/** A page that could not load: the reason and Try again, which loads the page afresh (nothing on it to lose yet). */
export function LoadError(p: { error: unknown }) {
  return <ErrorBox error={p.error} onRetry={() => location.reload()} />;
}

/** Toast with optional Undo (FLOW-1906 style). */
export interface ToastMsg {
  text: string;
  undo?: () => void | Promise<void>;
  undoLabel?: string;
}
let pushToast: ((t: ToastMsg) => void) | null = null;
export const toast = (t: ToastMsg) => pushToast?.(t);

export function Toaster() {
  const tr = useT();
  const [t, setT] = useState<ToastMsg | null>(null);
  useEffect(() => {
    pushToast = setT;
    return () => {
      pushToast = null;
    };
  }, []);
  useEffect(() => {
    if (!t) return;
    const id = setTimeout(() => setT(null), 10_000);
    return () => clearTimeout(id);
  }, [t]);
  return (
    <div class="toaster" role="status" aria-live="polite">
      {t ? (
        <div class="toast">
          <span>{t.text}</span>
          {t.undo ? (
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              onClick={async () => {
                const u = t.undo;
                setT(null);
                await u?.();
              }}
            >
              {t.undoLabel ?? tr.common.undo}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Keeps Tab inside an open dialog or side sheet (the last element wraps to the first and back). */
export function trapTab(box: HTMLElement, e: KeyboardEvent): void {
  if (e.key !== 'Tab') return;
  const f = [...box.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((el) => !el.hasAttribute('disabled') && el.getClientRects().length > 0);
  if (!f.length) return;
  const to = e.shiftKey ? (document.activeElement === f[0] || !box.contains(document.activeElement) ? f.at(-1) : null) : document.activeElement === f.at(-1) ? f[0] : null;
  if (!to) return;
  e.preventDefault();
  to.focus();
}

/**
 * Modal dialog: focus moves in on open and back on close, Escape cancels, Tab stays inside. A click on the backdrop
 * cancels too, but not once something has been typed: a stray click must not throw away a filled-in form.
 */
export function Dialog(p: { title: string; onClose: () => void; children: ComponentChildren; foot: ComponentChildren; sub?: ComponentChildren; onSubmit?: () => void; wide?: boolean }) {
  const ref = useRef<HTMLElement>(null);
  const dirty = useRef(false);
  useEffect(() => {
    const back = document.activeElement as HTMLElement | null;
    const box = ref.current!;
    const first = box.querySelector<HTMLElement>('input, select, textarea, button, [href]');
    (first ?? box).focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' && e.key !== 'Tab') return;
      e.stopPropagation(); // a dialog opened from a dialog closes alone
      if (e.key === 'Escape') p.onClose();
      else trapTab(box, e);
    };
    const onInput = () => (dirty.current = true);
    box.addEventListener('keydown', onKey);
    box.addEventListener('input', onInput);
    return () => {
      box.removeEventListener('keydown', onKey);
      box.removeEventListener('input', onInput);
      back?.focus();
    };
  }, []);
  const inner = (
    <>
      <h2 id="dlg-h" class="blk-title">
        {p.title}
      </h2>
      {p.sub ? <p class="sub">{p.sub}</p> : null}
      {p.children}
      <div class="modal-foot">{p.foot}</div>
    </>
  );
  const submit = p.onSubmit;
  return (
    <div class="modal-backdrop" role="presentation" onClick={(e) => e.target === e.currentTarget && !dirty.current && p.onClose()}>
      {submit ? (
        <form
          class={p.wide ? 'modal modal--wide' : 'modal'}
          role="dialog"
          aria-modal="true"
          aria-labelledby="dlg-h"
          tabIndex={-1}
          noValidate
          ref={ref as Ref<HTMLFormElement>}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          {inner}
        </form>
      ) : (
        <div class={p.wide ? 'modal modal--wide' : 'modal'} role="dialog" aria-modal="true" aria-labelledby="dlg-h" tabIndex={-1} ref={ref as Ref<HTMLDivElement>}>
          {inner}
        </div>
      )}
    </div>
  );
}

/**
 * Confirm a consequential action in the Hub's own dialog (replaces window.confirm). `danger` paints the button red
 * for removals; if the action fails the dialog stays open with the reason, so nothing fails silently.
 */
export function ConfirmDialog(p: { title: string; body: ComponentChildren; confirmLabel: string; danger?: boolean; onConfirm: () => void | Promise<void>; onClose: () => void }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  return (
    <Dialog
      title={p.title}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.common.cancel}
          </button>
          <button
            type="button"
            class={`btn ${p.danger ? 'btn-danger-solid' : 'btn-primary'}`}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await p.onConfirm();
                p.onClose();
              } catch (e) {
                setErr(e);
              } finally {
                setBusy(false);
              }
            }}
          >
            {p.confirmLabel}
          </button>
        </>
      }
    >
      <div class="stack">
        {p.body}
        {err ? <ErrorBox error={err} /> : null}
      </div>
    </Dialog>
  );
}

/** A row's ⋯ menu: one button, the actions inside (UX-012). */
export function RowMenu(p: { label: string; items: { label: string; run: () => void; danger?: boolean }[] }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    wrap.current?.querySelector<HTMLElement>('[role="menu"] button')?.focus();
    const onDown = (e: Event) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      btn.current?.focus();
    };
    addEventListener('pointerdown', onDown);
    addEventListener('keydown', onKey);
    return () => {
      removeEventListener('pointerdown', onDown);
      removeEventListener('keydown', onKey);
    };
  }, [open]);
  if (!p.items.length) return null;
  return (
    <div class="menu-wrap" ref={wrap}>
      <button type="button" class="btn btn-sm" ref={btn} aria-haspopup="menu" aria-expanded={open} aria-label={p.label} onClick={(e) => (e.stopPropagation(), setOpen(!open))}>
        ⋯
      </button>
      {open ? (
        <div class="menu" role="menu">
          {p.items.map((i) => (
            <button
              type="button"
              role="menuitem"
              class={`menu-item${i.danger ? ' is-danger' : ''}`}
              onClick={() => {
                setOpen(false);
                i.run();
              }}
            >
              {i.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** ‹ Month › stepper between two months (inclusive); the arrows stop at the ends. */
export function MonthStepper(p: { value: string; min: string; max: string; label: (m: string) => string; onChange: (m: string) => void; prevLabel: string; nextLabel: string; groupLabel: string; tour?: string }) {
  const shift = (m: string, d: number) => {
    const [y, mo] = m.split('-').map(Number) as [number, number];
    const t = y * 12 + (mo - 1) + d;
    return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
  };
  return (
    <span class="month-step" role="group" aria-label={p.groupLabel} data-tour={p.tour}>
      <button type="button" class="btn btn-sm" aria-label={p.prevLabel} disabled={p.value <= p.min} onClick={() => p.onChange(shift(p.value, -1))}>
        ‹
      </button>
      <strong aria-live="polite">{p.label(p.value)}</strong>
      <button type="button" class="btn btn-sm" aria-label={p.nextLabel} disabled={p.value >= p.max} onClick={() => p.onChange(shift(p.value, 1))}>
        ›
      </button>
    </span>
  );
}
